import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BluetoothAudioOutlinedIcon from "@mui/icons-material/BluetoothAudioOutlined";
import WifiPasswordRoundedIcon from "@mui/icons-material/WifiPasswordRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import WifiIcon from "@mui/icons-material/Wifi";

// ====== BLE UUIDs (기존 그대로) ======
const SERVICE_UUID = "7fe51376-ab4a-4b16-915e-a2619c62e1b3";
const WIFI_CONNECT_CHAR_UUID = "e3b87baa-322d-46ed-98db-0ac0eca143d1";
const RESPONSE_NOTIFY_CHAR_UUID = "e3b87baa-322d-46ed-98db-0ac0eca143d2";
const WIFI_DISCOVERY_CHAR_UUID = "e3b87baa-322d-46ed-98db-0ac0eca143d3";
const DEVICE_CONFIG_SETTING_CHAR_UUID = "e3b87baa-322d-46ed-98db-0ac0eca143d4";
const DEVICE_CONFIG_READ_CHAR_UUID = "e3b87baa-322d-46ed-98db-0ac0eca143d5";
const DEVICE_IP_READ_CHAR_UUID = "e3b87baa-322d-46ed-98db-0ac0eca143d6";

// ====== NOTIFY 코드 (기존 그대로) ======
const NOTIFY_CODE_ERROR = 901;
const NOTIFY_CODE_WIFI_CONNECTION_SUCCESS = 100;
const NOTIFY_CODE_WIFI_CONNECTION_FAILED = 101;
const NOTIFY_CODE_DEVICE_CONFIG_SUCCESS = 201;
const NOTIFY_CODE_DEVICE_CONFIG_FAILED = 202;

const steps = ["기기 연결", "WiFi 설정", "네트워크 상태", "고급 설정"];

function decodeUtf8(value) {
  const decoder = new TextDecoder("utf-8");
  return decoder.decode(value);
}

function App() {
  // ====== Step 상태 ======
  const [activeStep, setActiveStep] = useState(0);

  // ====== 연결/상태 ======
  const [device, setDevice] = useState(null);
  const [server, setServer] = useState(null);

  const [status, setStatus] = useState({
    level: "info", // info | success | error | warning
    text: "대기 중",
  });

  const isConnected = useMemo(() => !!server?.connected, [server]);

  const parseIPs = (raw) =>
    (raw || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((ip) => ip !== "127.0.0.1"); // 필요 없으면 제거

  // ====== Characteristics ======
  const [notifyCharacteristic, setNotifyCharacteristic] = useState(null);
  const [ssidListCharacteristic, setSsidListCharacteristic] = useState(null);
  const [ipListCharacteristic, setIPListCharacteristic] = useState(null);
  const [connectWifiCharacteristic, setConnectWifiCharacteristic] =
    useState(null);
  const [readConfigCharacteristic, setReadConfigCharacteristic] =
    useState(null);
  const [configCharacteristic, setConfigCharacteristic] = useState(null);

  // ====== WiFi 데이터 ======
  const [ssidList, setSsidList] = useState([]);
  const [selectedSsid, setSelectedSsid] = useState("");
  const [password, setPassword] = useState("");
  const [wifiOpen, setWifiOpen] = useState(false);

  // ====== IP ======
  const [ipList, setIPList] = useState("");

  // ====== Config ======
  const [config, setConfig] = useState(null);
  const [configExpanded, setConfigExpanded] = useState(false);

  const pwRef = useRef(null);
  const reconnectingRef = useRef(false);
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [wifiBusy, setWifiBusy] = useState(false);
  const [ipBusy, setIpBusy] = useState(false);
  const [configBusy, setConfigBusy] = useState(false);

  const isAnyBusy = deviceBusy || wifiBusy || ipBusy || configBusy;

  const wakeLockRef = useRef(null);

  useEffect(() => {
    const enable = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch (e) {
        console.log("WakeLock error:", e);
      }
    };

    if (isConnected) enable();

    return () => {
      try {
        wakeLockRef.current?.release();
        wakeLockRef.current = null;
      } catch {}
    };
  }, [isConnected]);

  // ====== Notify 수신 ======
  useEffect(() => {
    if (!notifyCharacteristic) return;

    let mounted = true;

    const onChanged = (event) => {
      if (!mounted) return;

      const str = decodeUtf8(event.target.value);
      const code = parseInt(str);

      if (code === NOTIFY_CODE_WIFI_CONNECTION_SUCCESS) {
        setStatus({ level: "success", text: "WiFi 연결 성공" });
        // 성공하면 Step 3로 이동 + IP 자동 읽기
        setActiveStep((prev) => Math.max(prev, 2));
        void readIPList(); // auto
      } else if (code === NOTIFY_CODE_WIFI_CONNECTION_FAILED) {
        setStatus({
          level: "error",
          text: "WiFi 연결 실패 (SSID/비번 또는 신호 확인)",
        });
      } else if (code === NOTIFY_CODE_DEVICE_CONFIG_SUCCESS) {
        setStatus({ level: "success", text: "설정 업데이트 성공" });
      } else if (code === NOTIFY_CODE_DEVICE_CONFIG_FAILED) {
        setStatus({ level: "error", text: "설정 업데이트 실패" });
      } else if (code === NOTIFY_CODE_ERROR) {
        setStatus({ level: "error", text: "장치 오류 코드(901)" });
      } else {
        setStatus({ level: "info", text: `알림 코드: ${code}` });
      }
    };

    (async () => {
      try {
        await notifyCharacteristic.startNotifications();
        notifyCharacteristic.addEventListener(
          "characteristicvaluechanged",
          onChanged,
        );
      } catch (e) {
        console.error(e);
        setStatus({
          level: "warning",
          text: "Notify 시작 실패 (브라우저/권한 확인)",
        });
      }
    })();

    return () => {
      mounted = false;
      try {
        notifyCharacteristic.removeEventListener(
          "characteristicvaluechanged",
          onChanged,
        );
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyCharacteristic]);

  // ====== 연결 끊김 감지 + 자동 재연결 ======
  useEffect(() => {
    if (!device) return;

    const onDisconnected = async () => {
      // 중복 재연결 방지
      if (reconnectingRef.current) return;
      reconnectingRef.current = true;

      setStatus({
        level: "warning",
        text: "연결이 끊겼습니다. 자동 재연결 중...",
      });

      // ✅ 여기서 절대 requestDevice()를 다시 부르지 않습니다.
      // 이미 선택된 device로만 재연결 시도
      for (let i = 0; i < 5; i++) {
        try {
          await new Promise((r) => setTimeout(r, 800));

          const gattServer = await device.gatt.connect();
          setServer(gattServer);

          const ok = await getServiceAndCharacteristics(gattServer);
          if (!ok) throw new Error("Service/Characteristic reload failed");

          setStatus({ level: "success", text: "재연결 완료" });

          // 이전 단계 유지 (최소 WiFi 단계까지는 유지하도록)
          setActiveStep((prev) => Math.max(prev, 1));

          reconnectingRef.current = false;
          return;
        } catch (e) {
          console.log("reconnect failed:", i, e);
        }
      }

      // 여기까지 오면 재연결 실패
      setStatus({
        level: "error",
        text: "재연결 실패. Scan Device로 다시 연결해주세요.",
      });
      setServer(null);
      setActiveStep(0);

      reconnectingRef.current = false;
    };

    device.addEventListener("gattserverdisconnected", onDisconnected);
    return () =>
      device.removeEventListener("gattserverdisconnected", onDisconnected);
  }, [device]);

  // ====== Step 1: Device scan + connect + getService ======
  const connectFlow = async () => {
    try {
      setDeviceBusy(true);
      setStatus({ level: "info", text: "기기를 찾는 중..." });

      const selected = await navigator.bluetooth.requestDevice({
        filters: [{ services: [SERVICE_UUID] }],
      });
      setDevice(selected);

      setStatus({
        level: "info",
        text: `연결 중: ${selected.name || "Unknown"}`,
      });
      const gattServer = await selected.gatt.connect();
      setServer(gattServer);

      const ok = await getServiceAndCharacteristics(gattServer);
      if (!ok) {
        setStatus({ level: "error", text: "서비스/Characteristic 로드 실패" });
        return;
      }

      setStatus({
        level: "success",
        text: `연결됨: ${selected.name || "Device"}`,
      });
      setActiveStep(1);

      // Step2 들어가자마자 WiFi 목록 자동 스캔(원치 않으면 주석)
      void readSsidList();
      // Config도 미리 읽어두고 싶으면 아래 주석 해제
      // void readConfig();
    } catch (e) {
      console.error(e);
      setStatus({ level: "error", text: e?.message || "연결 실패" });
    } finally {
      setDeviceBusy(false);
    }
  };

  const getServiceAndCharacteristics = async (gattServer) => {
    try {
      const service = await gattServer.getPrimaryService(SERVICE_UUID);

      const ssidChar = await service.getCharacteristic(
        WIFI_DISCOVERY_CHAR_UUID,
      );
      const notifyChar = await service.getCharacteristic(
        RESPONSE_NOTIFY_CHAR_UUID,
      );
      const wifiConnectChar = await service.getCharacteristic(
        WIFI_CONNECT_CHAR_UUID,
      );
      const readCfgChar = await service.getCharacteristic(
        DEVICE_CONFIG_READ_CHAR_UUID,
      );
      const cfgChar = await service.getCharacteristic(
        DEVICE_CONFIG_SETTING_CHAR_UUID,
      );
      const ipChar = await service.getCharacteristic(DEVICE_IP_READ_CHAR_UUID);

      setSsidListCharacteristic(ssidChar);
      setNotifyCharacteristic(notifyChar);
      setConnectWifiCharacteristic(wifiConnectChar);
      setReadConfigCharacteristic(readCfgChar);
      setConfigCharacteristic(cfgChar);
      setIPListCharacteristic(ipChar);

      return true;
    } catch (e) {
      console.error("getService error:", e);
      return false;
    }
  };

  // ====== Step 2: WiFi scan ======
  const readSsidList = async () => {
    if (!ssidListCharacteristic) return;

    try {
      setWifiBusy(true);
      setStatus({ level: "info", text: "WiFi 목록 스캔 중..." });

      const value = await ssidListCharacteristic.readValue();
      const jsonString = decodeUtf8(value);

      // 장치가 JSON을 chunk로 보내는 경우가 있으면 별도 로직이 필요합니다.
      // 일단 기존 구현 그대로 JSON.parse 시도
      const jsonData = JSON.parse(jsonString);

      const list = (jsonData?.data || [])
        .map((s) => (typeof s === "string" ? s.trim() : ""))
        .filter((s) => s);

      setSsidList(list);

      // 선택값이 없으면 첫 항목 자동 선택(원치 않으면 제거)
      if (!selectedSsid && list.length > 0) setSelectedSsid(list[0]);

      setStatus({ level: "success", text: "WiFi 목록을 가져왔습니다." });
    } catch (e) {
      console.error(e);
      setStatus({
        level: "error",
        text: "WiFi 목록 읽기 실패 (JSON/데이터 형식 확인)",
      });
    } finally {
      setWifiBusy(false);
    }
  };

  // ====== Step 2: WiFi connect ======
  const sendSsidAndPassword = async () => {
    if (!connectWifiCharacteristic) return;
    if (!selectedSsid) {
      setStatus({ level: "warning", text: "SSID를 선택해주세요." });
      return;
    }

    const pwRaw = pwRef.current?.value || "";
    const pw = wifiOpen ? "" : pwRaw.trim();

    if (!wifiOpen && !pw) {
      setStatus({ level: "warning", text: "비밀번호를 입력해주세요." });
      return;
    }

    try {
      setWifiBusy(true);
      setStatus({ level: "info", text: "WiFi 연결 요청 중..." });

      const encoder = new TextEncoder("utf-8");

      // ✅ 오픈 WiFi는 "SSID," 형태로 전송 (펌웨어가 이 형식을 받는 경우가 가장 많습니다)
      const payload = `${selectedSsid},${pw}`;

      await connectWifiCharacteristic.writeValue(encoder.encode(payload));

      setStatus({ level: "info", text: "장치 응답 대기 중..." });
    } catch (e) {
      console.error(e);
      setStatus({ level: "error", text: "WiFi 연결 명령 전송 실패" });
    } finally {
      setWifiBusy(false);
    }
  };
  // ====== Step 3: IP ======
  const readIPList = async () => {
    if (!ipListCharacteristic) return;

    try {
      setIpBusy(true);
      setStatus({ level: "info", text: "IP 확인 중..." });

      const value = await ipListCharacteristic.readValue();
      const data = decodeUtf8(value);
      setIPList(data);

      setStatus({ level: "success", text: "IP를 확인했습니다." });
      setActiveStep((prev) => Math.max(prev, 2));
    } catch (e) {
      console.error(e);
      setStatus({ level: "error", text: "IP 읽기 실패" });
    } finally {
      setIpBusy(false);
    }
  };

  // ====== Step 4: Config read/update ======
  const readConfig = async () => {
    if (!readConfigCharacteristic) return;

    try {
      setConfigBusy(true);
      setStatus({ level: "info", text: "설정 읽는 중..." });

      const value = await readConfigCharacteristic.readValue();
      const jsonString = decodeUtf8(value);
      const jsonData = JSON.parse(jsonString);

      setConfig(jsonData);
      setStatus({ level: "success", text: "설정을 가져왔습니다." });
      setActiveStep((prev) => Math.max(prev, 3));
    } catch (e) {
      console.error(e);
      setStatus({
        level: "error",
        text: "설정 읽기 실패 (JSON/데이터 형식 확인)",
      });
    } finally {
      setConfigBusy(false);
    }
  };

  const sendConfig = async () => {
    if (!configCharacteristic || !config) return;

    try {
      setConfigBusy(true);
      setStatus({ level: "info", text: "설정 업데이트 중..." });

      const encoder = new TextEncoder("utf-8");
      const jsonData = JSON.stringify(config);
      await configCharacteristic.writeValue(encoder.encode(jsonData));

      // 성공/실패는 Notify(201/202)로 들어옴
      setStatus({ level: "info", text: "장치 응답 대기 중..." });
    } catch (e) {
      console.error(e);
      setStatus({ level: "error", text: "설정 전송 실패" });
    } finally {
      setConfigBusy(false);
    }
  };

  // ====== UI Helpers ======
  const StatusBanner = () => (
    <Alert
      severity={status.level}
      sx={{
        borderRadius: 2,
        "& .MuiAlert-message": {
          wordBreak: "keep-all",
          whiteSpace: "normal",
        },
      }}
    >
      {status.text}
    </Alert>
  );

  const StepCard = ({ title, icon, right, children }) => (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          mb={1}
          sx={{
            flexWrap: "wrap",
            rowGap: 1,
            "& .MuiTypography-root": { minWidth: 0 }, // 글씨 잘림 방지
          }}
        >
          {icon}
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
              whiteSpace: "normal",
              wordBreak: "keep-all",
            }}
          >
            {title}
          </Typography>
          <Box sx={{ ml: "auto" }}>{right}</Box>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Container
      maxWidth="sm"
      disableGutters
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight={900}>
          Hyosim Setup
        </Typography>

        <StatusBanner />

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            px: { xs: 0, sm: 1 },
            "& .MuiStepLabel-label": {
              typography: { xs: "caption", sm: "body2" },
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* ===== Step 1 ===== */}
        <StepCard
          title="1단계 · 기기 연결"
          icon={<BluetoothAudioOutlinedIcon color="primary" />}
          right={
            isConnected ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Connected"
                color="success"
              />
            ) : (
              <Chip icon={<ErrorOutlineIcon />} label="Not Connected" />
            )
          }
        >
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              블루투스 장치를 선택해 연결합니다. 연결되면 자동으로 다음 단계가
              열립니다.
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                onClick={connectFlow}
                disabled={deviceBusy || isConnected}
                sx={{ borderRadius: 2, minWidth: 200 }}
              >
                {deviceBusy ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} color="inherit" />
                    <span>Connecting...</span>
                  </Stack>
                ) : isConnected ? (
                  "Connected"
                ) : (
                  "Scan Device"
                )}
              </Button>

              {device?.name && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    선택된 기기
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {device.name}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Stack>
        </StepCard>

        {/* ===== Step 2 ===== */}
        <StepCard
          title="2단계 · WiFi 설정"
          icon={<WifiPasswordRoundedIcon color="success" />}
          right={
            <Chip
              icon={<WifiIcon />}
              label={
                ssidList.length ? `${ssidList.length} networks` : "no list"
              }
              color={ssidList.length ? "primary" : "default"}
            />
          }
        >
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              WiFi 목록을 불러온 뒤, SSID를 선택하고 비밀번호를 입력해
              연결합니다.
            </Typography>

            <Stack
              spacing={1.5}
              sx={{
                "& .MuiButton-root": { borderRadius: 2 },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={readSsidList}
                disabled={!ssidListCharacteristic || wifiBusy || !isConnected}
                fullWidth
              >
                WIFI 목록 새로고침
              </Button>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={wifiOpen}
                    onChange={(e) => setWifiOpen(e.target.checked)}
                  />
                }
                label="비밀번호 없음 (오픈 WiFi)"
              />

              <FormControl fullWidth disabled={!isConnected || wifiBusy}>
                <InputLabel id="ssid-select-label">SSID</InputLabel>
                <Select
                  labelId="ssid-select-label"
                  label="SSID"
                  value={selectedSsid}
                  onChange={(e) => setSelectedSsid(e.target.value)}
                >
                  {ssidList.length === 0 ? (
                    <MenuItem value="">
                      <em>목록 없음</em>
                    </MenuItem>
                  ) : (
                    ssidList.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <TextField
                label="Password"
                type="password"
                inputRef={pwRef}
                disabled={!isConnected || wifiOpen}
                autoComplete="new-password"
                fullWidth
                sx={{ flex: 1, minWidth: 200 }}
              />

              <Button
                variant="contained"
                onClick={sendSsidAndPassword}
                disabled={
                  !connectWifiCharacteristic || wifiBusy || !isConnected
                }
                fullWidth
                sx={{ py: 1.2, fontWeight: 800 }}
              >
                {wifiBusy ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} color="inherit" />
                    <span>Sending...</span>
                  </Stack>
                ) : (
                  "Connect WiFi"
                )}
              </Button>
            </Stack>

            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              ※ 연결 결과는 장치 Notify 코드(100/101)로 표시됩니다.
            </Typography>
          </Stack>
        </StepCard>

        {/* ===== Step 3 ===== */}
        <StepCard
          title="3단계 · 네트워크 상태"
          icon={<WifiIcon color="primary" />}
          right={
            <Chip
              icon={ipList ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
              label={ipList ? "IP Ready" : "No IP"}
              color={ipList ? "success" : "default"}
            />
          }
        >
          <Stack spacing={2}>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", wordBreak: "keep-all" }}
            >
              WiFi 연결 성공 후 IP를 확인합니다. (성공 시 자동으로 읽지만,
              필요하면 수동으로도 가능합니다.)
            </Typography>

            <Stack direction="column" spacing={1} alignItems="stretch">
              {/* IP 다시 읽기 */}
              <Button
                variant="outlined"
                onClick={readIPList}
                disabled={!ipListCharacteristic || ipBusy || !isConnected}
                fullWidth
                sx={{
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                  minWidth: { sm: 140 },
                }}
              >
                IP 다시 읽기
              </Button>

              {/* IP Chip 영역 */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  IP Addresses
                </Typography>

                {parseIPs(ipList).length === 0 ? (
                  <Typography variant="body2" fontWeight={700}>
                    -
                  </Typography>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mt: 0.5 }}
                  >
                    {parseIPs(ipList).map((ip) => (
                      <Chip
                        key={ip}
                        label={ip}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              {/* 고급 설정 */}
              <Button
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => {
                  setActiveStep(3);
                  if (!config) void readConfig();
                  setConfigExpanded(true);
                }}
                disabled={!isConnected}
                fullWidth
                sx={{
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                  minWidth: { sm: 160 },
                }}
              >
                고급 설정
              </Button>
            </Stack>
          </Stack>
        </StepCard>

        {/* ===== Step 4 ===== */}
        <StepCard
          title="4단계 · 고급 설정"
          icon={<SettingsIcon color="action" />}
          right={
            <Chip
              label={config ? "Loaded" : "Not Loaded"}
              color={config ? "success" : "default"}
            />
          }
        >
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              기본 설치자는 보통 건드릴 필요가 없습니다. 필요한 경우에만 펼쳐서
              수정하세요.
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={readConfig}
                disabled={
                  !readConfigCharacteristic || configBusy || !isConnected
                }
                sx={{ borderRadius: 2 }}
              >
                설정 불러오기
              </Button>

              <Button
                variant="contained"
                onClick={sendConfig}
                disabled={
                  !configCharacteristic || !config || configBusy || !isConnected
                }
                sx={{ borderRadius: 2 }}
              >
                설정 저장
              </Button>
            </Stack>

            <Accordion
              expanded={configExpanded}
              onChange={(_, v) => setConfigExpanded(v)}
              disabled={!isConnected}
              sx={{ borderRadius: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={800}>설정 편집 (Advanced)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {!config ? (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    아직 설정을 불러오지 않았습니다. “설정 불러오기”를
                    눌러주세요.
                  </Typography>
                ) : (
                  <Box>
                    {Object.keys(config).map((category) => (
                      <Box key={category} sx={{ mb: 2 }}>
                        <Typography fontWeight={900} sx={{ mb: 1 }}>
                          {category.replace(/_/g, " ")}
                        </Typography>

                        {config?.[category] &&
                          typeof config[category] === "object" &&
                          Object.keys(config[category]).map((key) => (
                            <TextField
                              key={key}
                              label={key.replace(/_/g, " ")}
                              value={config[category][key]}
                              onChange={(e) =>
                                setConfig((prev) => ({
                                  ...prev,
                                  [category]: {
                                    ...prev[category],
                                    [key]: e.target.value,
                                  },
                                }))
                              }
                              fullWidth
                              size="small"
                              sx={{ mb: 1.2 }}
                            />
                          ))}
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Stack>
        </StepCard>
      </Stack>
    </Container>
  );
}

export default App;
